/**
 * Location detail screen (DNKADM-0051 U1/U3; cards DNKADM-0495) — one owned `v2_location`,
 * routed at `/locations/:locationId` under `RequireAdmin`. The old `/hours` and `/tax` tab
 * links still open this page (the router keeps them).
 *
 * **Read-only cards, one Edit each (DNKADM-0495, the DNKADM-0493 pattern).** Each card's
 * single Edit opens a right-side drawer with one Save (or Done). Nothing outside a drawer
 * saves. The header holds the name, the status chip and a More menu (Turn off / Turn on:
 * the same deactivate / reactivate calls the list makes, with the same plan-limit handling).
 *
 * - **Basics** — name, address, map link, time zone, phone, plus the other fields the
 *   location form carries (default location, what it calls a bay, billing day). The drawer
 *   is the existing {@link LocationDialog} form in drawer mode: same fields, same PUT body.
 * - **Open hours** — the weekly hours (`getOperatingHours`). The drawer hosts the existing
 *   {@link OperatingHoursGrid} (embedded: the drawer's Save sends the same whole-week PUT).
 * - **Days closed** — the date exceptions (`getHoursExceptions`). The drawer hosts the
 *   existing {@link HoursExceptionsEditor} (embedded: the drawer's Save sends the same
 *   whole-list PUT).
 * - **Bays at this location** — read-only: the active bays whose list row names this
 *   location (`listResources`, the Bays list's own read, whose rows carry their locations).
 *   Each links to its page. No Edit.
 * - **Tax** — the active tax rates (`listTaxRates`). The drawer hosts the existing
 *   {@link TaxRatesEditor} unchanged (it adds and edits one rate at a time), so its footer
 *   is Done.
 *
 * The single `mapUrl` (DNKAPI-0164) renders as a safe **Open in Maps** new-tab link —
 * clickable **only** when its scheme is `http`/`https` (anything else renders as plain
 * text, never an active link). DNKADM-0081 transition: when `mapUrl` is absent but the
 * pre-0164 geo coords still arrive, it falls back to the old coord-built **Directions**
 * link. A load failure shows error + Retry; a missing location (`404`) shows a clear
 * not-found note with a link back to the list. A failed or pending card read shows
 * "Couldn't load" or "—", never a false "none".
 */
import { useCallback, useRef, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Link as MuiLink,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { IconAction } from '@/components/IconAction';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import {
  deactivateLocation,
  getLocation,
  reactivateLocation,
  type V2Location,
} from '@/api/locations';
import {
  getHoursExceptions,
  getOperatingHours,
  type HoursException,
  type WeeklyHours,
} from '@/api/operatingHours';
import { listResources, type V2Resource } from '@/api/resources';
import { listTaxRates, type TaxRate } from '@/api/taxRates';
import { dateLabel } from '@/utils/clockTime';
import { SummaryCard, SummaryRow } from '@/features/services/SummaryCard';
import { EditDrawer } from '@/features/services/EditDrawer';
import {
  OperatingHoursGrid,
  type OperatingHoursGridHandle,
} from '@/features/operating-hours/OperatingHoursGrid';
import {
  HoursExceptionsEditor,
  type HoursExceptionsEditorHandle,
} from '@/features/operating-hours/HoursExceptionsEditor';
import { hasOpenDay, shiftsLabel, weekLines } from '@/features/operating-hours/hoursSummary';
import { bayNounPlural, capitalize } from '@/features/resources/bayNoun';
import { LocationDialog } from './LocationDialog';
import { TaxRatesEditor } from './TaxRatesEditor';
import { ScreenError } from '@/components/ScreenError';

/**
 * Whether a freeform `mapUrl` is a safe-to-link `http`/`https` URL. `mapUrl` is
 * untrusted admin input (DNKAPI-0164) — only an `http(s)` value is rendered as an
 * active link; anything else (a `javascript:`/other scheme, or an unparseable string)
 * renders as plain text, never a clickable link.
 */
function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * DNKADM-0081 transition fallback: a Google Maps Directions URL from the location's
 * pre-0164 geo coords (or null when none) — a `googlePlaceId` is preferred, otherwise
 * the lat/long pair. Used ONLY when `mapUrl` is absent; once DNKAPI-0164 is live the
 * coords stop arriving and this (with the deprecated coord fields) can be deleted.
 */
function directionsUrl(location: V2Location): string | null {
  if (location.googlePlaceId !== undefined) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      location.googlePlaceId,
    )}&query_place_id=${encodeURIComponent(location.googlePlaceId)}`;
  }
  if (location.latitude !== undefined && location.longitude !== undefined) {
    return `https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`;
  }
  return null;
}

/**
 * The location's map affordance: prefer the freeform `mapUrl` (DNKAPI-0164) — an
 * `http(s)` value renders as a safe new-tab "Open in Maps" link, a non-`http(s)`/
 * unparseable value renders as plain text (never an active link). When `mapUrl` is
 * absent, DNKADM-0081's transition fallback renders the pre-0164 coord-built
 * Directions link. Renders nothing when neither is available.
 */
function LocationMapLink({ location }: { location: V2Location }) {
  if (location.mapUrl !== undefined) {
    if (isHttpUrl(location.mapUrl)) {
      return (
        <MuiLink
          href={location.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}
        >
          <Icon name="map" sx={{ fontSize: 18 }} />
          Open in Maps
        </MuiLink>
      );
    }
    // Untrusted non-http(s) value — show it as plain text, never a clickable link.
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
        {location.mapUrl}
      </Typography>
    );
  }

  // Transition fallback (DNKADM-0081): no mapUrl yet, but the pre-0164 coords arrived.
  const fallback = directionsUrl(location);
  if (fallback) {
    return (
      <MuiLink
        href={fallback}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13 }}
      >
        <Icon name="directions" sx={{ fontSize: 18 }} />
        Directions
      </MuiLink>
    );
  }
  return null;
}

/** Whether the location has any map affordance to show (mirrors {@link LocationMapLink}). */
function hasMap(location: V2Location): boolean {
  return location.mapUrl !== undefined || directionsUrl(location) !== null;
}

/** Render a rate percentage at two decimals (`6` → `6.00%`), as the tax editor does. */
function percentLabel(ratePercent: number): string {
  return `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(ratePercent)}%`;
}

type CardDrawer = 'basics' | 'hours' | 'closed' | 'tax';

export function LocationDetail() {
  const { locationId = '' } = useParams<{ locationId: string }>();
  const showToast = useToast();

  const location = useResource(
    useCallback(() => getLocation(locationId), [locationId]),
    [locationId],
  );
  const hours = useResource(
    useCallback(() => getOperatingHours(locationId), [locationId]),
    [locationId],
  );
  const exceptions = useResource(
    useCallback(() => getHoursExceptions(locationId), [locationId]),
    [locationId],
  );
  // The active bays here: the Bays list's own read, whose rows carry their locations inline.
  const bays = useResource(
    useCallback(async (): Promise<V2Resource[] | null> => {
      const rows = await listResources({ status: 'active' });
      // A row that does not say where it is (locations absent) can't be placed; when no row
      // says, the card shows "—" rather than a false "none here".
      if (rows.length > 0 && rows.every((r) => r.locations === undefined)) return null;
      return rows.filter((r) => r.locations?.some((l) => l.id === locationId) ?? false);
    }, [locationId]),
    [locationId],
  );
  const taxRates = useResource(
    useCallback(() => listTaxRates(locationId, { status: 'active' }), [locationId]),
    [locationId],
  );

  // ---- Card drawers ----
  const [drawer, setDrawer] = useState<CardDrawer | null>(null);
  const hoursRef = useRef<OperatingHoursGridHandle>(null);
  const exceptionsRef = useRef<HoursExceptionsEditorHandle>(null);
  const closeDrawer = () => setDrawer(null);

  // ---- Header actions (More menu) ----
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  async function handleTurnOff(loc: V2Location) {
    setMenuAnchor(null);
    try {
      await deactivateLocation(loc.id);
      location.refetch();
      showToast('Location turned off', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t turn the location off.', 'error');
    }
  }

  async function handleTurnOn(loc: V2Location) {
    setMenuAnchor(null);
    try {
      await reactivateLocation(loc.id);
      location.refetch();
      showToast('Location turned on', 'success');
    } catch (err) {
      // Reactivate consumes a plan slot, so it is plan-limit enforced exactly as on the
      // list (DNKADM-0065 / DNKAPI-0139).
      const code = v2ErrorCode(err);
      if (code === 'PLAN_LIMIT_REACHED') {
        setPlanLimit(parsePlanLimit(err));
        return;
      }
      if (code === 'PLAN_VERIFY_FAILED') {
        showToast('We couldn’t verify your plan just now. Please try again in a moment.', 'error');
        return;
      }
      showToast(serverMessage(err) ?? 'Couldn’t turn the location on.', 'error');
    }
  }

  /** The drawer's Save for an embedded editor: its result, plus the one success toast. */
  const saveVia =
    (ref: { current: { save: () => Promise<boolean> } | null }) => async (): Promise<boolean> => {
      const ok = (await ref.current?.save()) ?? false;
      if (ok) showToast('Changes saved', 'success');
      return ok;
    };

  const loc = location.data;

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/locations"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All locations
      </MuiLink>

      {location.status === 'loading' && !loc && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading location"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {location.status === 'error' && (
        <ScreenError
          error={location.error}
          what="the location"
          onRetry={location.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That location wasn’t found. It may have been removed.{' '}
              <MuiLink component={RouterLink} to="/locations">
                Back to locations
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {/* While the page re-reads after a drawer saves, the last values stay on screen. */}
      {location.status !== 'error' && loc && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Typography variant="h6" component="h1">
              {loc.name === '' ? loc.id : loc.name}
            </Typography>
            <Chip
              size="small"
              label={loc.isActive ? 'Active' : 'Inactive'}
              color={loc.isActive ? 'success' : 'default'}
              variant={loc.isActive ? 'filled' : 'outlined'}
            />
            <Box sx={{ flex: 1 }} />
            <IconAction label="More actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <Icon name="more_vert" />
            </IconAction>
            <Menu
              anchorEl={menuAnchor}
              open={menuAnchor !== null}
              onClose={() => setMenuAnchor(null)}
            >
              {loc.isActive ? (
                <MenuItem onClick={() => void handleTurnOff(loc)}>Turn off</MenuItem>
              ) : (
                <MenuItem onClick={() => void handleTurnOn(loc)}>Turn on</MenuItem>
              )}
            </Menu>
          </Stack>

          <Stack spacing={2} sx={{ maxWidth: 880, mb: 4 }}>
            <BasicsCard location={loc} onEdit={() => setDrawer('basics')} />

            <OpenHoursCard
              hours={hours.data}
              failed={hours.status === 'error' && hours.data === null}
              onEdit={() => setDrawer('hours')}
            />

            <DaysClosedCard
              exceptions={exceptions.data}
              failed={exceptions.status === 'error' && exceptions.data === null}
              onEdit={() => setDrawer('closed')}
            />

            <BaysCard
              plural={bayNounPlural([loc])}
              bays={bays.status === 'error' ? undefined : bays.data}
            />

            <TaxCard
              rates={taxRates.data}
              failed={taxRates.status === 'error' && taxRates.data === null}
              onEdit={() => setDrawer('tax')}
            />
          </Stack>

          {/* ---- Drawers ---- */}
          <LocationDialog
            open={drawer === 'basics'}
            drawer
            location={loc}
            onClose={closeDrawer}
            onSaved={() => {
              closeDrawer();
              location.refetch();
            }}
            showToast={showToast}
          />

          <EditDrawer
            open={drawer === 'hours'}
            title="Edit open hours"
            onClose={closeDrawer}
            saveDisabled={hours.data === null}
            onSave={saveVia(hoursRef)}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              The weekly hours this location is open, in its local time.
            </Typography>
            {hours.data !== null && (
              <OperatingHoursGrid
                embedded
                handleRef={hoursRef}
                days={hours.data.days}
                locationId={locationId}
                refetch={hours.refetch}
                showToast={showToast}
              />
            )}
          </EditDrawer>

          <EditDrawer
            open={drawer === 'closed'}
            title="Edit days closed"
            onClose={closeDrawer}
            saveDisabled={exceptions.data === null}
            onSave={saveVia(exceptionsRef)}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Close the location on a date, or set special hours for it.
            </Typography>
            {exceptions.data !== null && (
              <HoursExceptionsEditor
                embedded
                handleRef={exceptionsRef}
                exceptions={exceptions.data}
                locationId={locationId}
                refetch={exceptions.refetch}
                showToast={showToast}
              />
            )}
          </EditDrawer>

          <EditDrawer
            open={drawer === 'tax'}
            title="Edit tax"
            onClose={() => {
              closeDrawer();
              taxRates.refetch();
            }}
            doneOnly
          >
            <TaxRatesEditor locationId={locationId} />
          </EditDrawer>

          <PlanLimitDialog
            open={planLimit !== null}
            info={planLimit}
            onClose={() => setPlanLimit(null)}
          />
        </>
      )}
    </Box>
  );
}

function BasicsCard({ location, onEdit }: { location: V2Location; onEdit: () => void }) {
  const singular = location.resourceNounSingular;
  const plural = location.resourceNounPlural;
  return (
    <SummaryCard title="Basics" onEdit={onEdit}>
      <SummaryRow label="Name" value={location.name === '' ? location.id : location.name} />
      <SummaryRow
        label="Address"
        value={
          location.address === undefined ? (
            'None'
          ) : (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {location.address}
            </Typography>
          )
        }
      />
      <SummaryRow
        label="Map link"
        value={hasMap(location) ? <LocationMapLink location={location} /> : 'None'}
      />
      <SummaryRow label="Time zone" value={location.timezone === '' ? '—' : location.timezone} />
      <SummaryRow label="Phone" value={location.phone ? location.phone : 'None'} />
      <SummaryRow
        label="Default location"
        value={location.isDefault ? 'Yes' : 'No'}
        hint="Preselected where members and staff choose a location."
      />
      <SummaryRow
        label="What it calls a bay"
        value={
          singular === undefined && plural === undefined
            ? 'Not set'
            : `${singular ?? '—'} / ${plural ?? '—'}`
        }
      />
      <SummaryRow
        label="Membership billing day"
        value={
          location.billingAnchorDay === undefined
            ? 'Same as your business'
            : `Day ${location.billingAnchorDay} of the month`
        }
      />
      <SummaryRow
        label="Status"
        value={location.isActive ? 'Active' : 'Inactive'}
        hint="Change it from More."
        last
      />
    </SummaryCard>
  );
}

function OpenHoursCard({
  hours,
  failed,
  onEdit,
}: {
  hours: WeeklyHours | null;
  failed: boolean;
  onEdit: () => void;
}) {
  if (hours !== null && !hasOpenDay(hours.days)) {
    return <SummaryCard title="Open hours" onEdit={onEdit} empty="No open hours set yet" />;
  }
  const lines = hours === null ? [] : weekLines(hours.days);
  return (
    <SummaryCard title="Open hours" onEdit={onEdit} editDisabled={hours === null}>
      {hours === null ? (
        <SummaryRow label="Hours" value={failed ? 'Couldn’t load' : '—'} last />
      ) : (
        lines.map((line, i) => (
          <SummaryRow
            key={line.days}
            label={line.days}
            value={line.hours}
            last={i === lines.length - 1}
          />
        ))
      )}
    </SummaryCard>
  );
}

function DaysClosedCard({
  exceptions,
  failed,
  onEdit,
}: {
  exceptions: HoursException[] | null;
  failed: boolean;
  onEdit: () => void;
}) {
  if (exceptions !== null && exceptions.length === 0) {
    return <SummaryCard title="Days closed" onEdit={onEdit} empty="No days closed" />;
  }
  return (
    <SummaryCard title="Days closed" onEdit={onEdit} editDisabled={exceptions === null}>
      {exceptions === null ? (
        <SummaryRow label="Dates" value={failed ? 'Couldn’t load' : '—'} last />
      ) : (
        exceptions.map((ex, i) => (
          <SummaryRow
            key={`${ex.exceptionDate}-${i}`}
            label={dateLabel(ex.exceptionDate) ?? ex.exceptionDate}
            value={ex.isAvailable ? `Special hours: ${shiftsLabel(ex.shifts)}` : 'Closed'}
            last={i === exceptions.length - 1}
          />
        ))
      )}
    </SummaryCard>
  );
}

/**
 * Read-only: the active bays here, each a link to its page. `bays` is `undefined` when the
 * read failed and `null` while loading or when the rows don't say where they are.
 */
function BaysCard({ plural, bays }: { plural: string; bays: V2Resource[] | null | undefined }) {
  const title = `${capitalize(plural)} at this location`;
  if (bays !== undefined && bays !== null && bays.length === 0) {
    return <SummaryCard title={title} onEdit={() => {}} noEdit empty={`No ${plural} here yet`} />;
  }
  return (
    <SummaryCard title={title} onEdit={() => {}} noEdit>
      {bays === undefined || bays === null ? (
        <SummaryRow
          label={capitalize(plural)}
          value={bays === undefined ? 'Couldn’t load' : '—'}
          last
        />
      ) : (
        bays.map((bay, i) => (
          <SummaryRow
            key={bay.id}
            label={bay.name.length > 0 ? bay.name : bay.id}
            value={
              <MuiLink component={RouterLink} to={`/resources/${bay.id}`} sx={{ fontSize: 14 }}>
                Open
              </MuiLink>
            }
            last={i === bays.length - 1}
          />
        ))
      )}
    </SummaryCard>
  );
}

function TaxCard({
  rates,
  failed,
  onEdit,
}: {
  rates: TaxRate[] | null;
  failed: boolean;
  onEdit: () => void;
}) {
  if (rates !== null && rates.length === 0) {
    return <SummaryCard title="Tax" onEdit={onEdit} empty="No tax rates" />;
  }
  return (
    <SummaryCard title="Tax" onEdit={onEdit}>
      {rates === null ? (
        <SummaryRow label="Tax rates" value={failed ? 'Couldn’t load' : '—'} last />
      ) : (
        rates.map((rate, i) => (
          <SummaryRow
            key={rate.id}
            label={rate.name}
            value={percentLabel(rate.ratePercent)}
            hint={rate.taxAgency}
            last={i === rates.length - 1}
          />
        ))
      )}
    </SummaryCard>
  );
}
